/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnInit, OnDestroy} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import { Stage } from '../../../../../model';
import { StageProvider } from 'src/app/providers';
import { SearchableItem } from 'src/app/model/interface/searchable.item';
import { PresetValue } from 'src/app/model/preset-value';
import { ReportType } from 'src/app/model/report';
import { SearchableSelection } from 'src/app/model/interface/searchable.selection';
import { ModeService } from 'src/app/services';
import { SystemType } from 'src/app/model/enums/system.type';

@Component({
  selector: 'new-report-modal',
  templateUrl: './new-report.modal.html',
  styleUrls: ['./new-report.modal.scss']
})
export class NewReportModal implements OnInit, OnDestroy {
  subject: Subject<any>;
  subscription: any;
  companyName = '';
  stageId = '';
  stages: Stage[];
  stagesValues: SearchableItem[];

  currentSystem: SystemType;
  modeSub: any;

  reportType: ReportType;

  repTypeValues: SearchableItem[];

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private stageProvider: StageProvider,
    private modeService: ModeService
  ) {}

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc')
        this.onCancel();
    });

    this.modeSub = this.modeService.mode$.subscribe(mode => {
      if(mode){
        this.currentSystem = mode;

        if (this.currentSystem === SystemType.BV){
          this.reportType = ReportType.CONCLUSION;

          this.repTypeValues = [
            PresetValue.create('Conclusion of Value', ReportType.CONCLUSION),
            PresetValue.create('Calculation of Value', ReportType.CALCULATION)
          ];
        }
        else if (this.currentSystem === SystemType.EA) {
          this.reportType = ReportType.NO_SITE_VISIT;

          this.repTypeValues = [
            PresetValue.create('No Site Visit', ReportType.NO_SITE_VISIT),
            PresetValue.create('With Site Visit', ReportType.WITH_SITE_VISIT),
            PresetValue.create('Liquor License Appraisal', ReportType.LIQUOR_LICENSE),
            PresetValue.create('Inventory Appraisal', ReportType.INVENTORY_APPRAISAL)
          ];
        }
      }
    });

    this.stageProvider.getStages(this.currentSystem).subscribe(data => {
      if (data.success) {
        this.stages = Stage.parseArray(data.result)
          .sort((a, b) => a.ORDER < b.ORDER ? 1 : 0)
          .filter(s => s.ORDER <= 3);

        if (!this.stagesValues) {
          this.stagesValues = [];
        }

        for (const s of this.stages) {
          this.stagesValues.push(PresetValue.create(s.NAME, s.ID ));
        }
        if(this.currentSystem === SystemType.BV)
          this.stageId = 'ORDR';
        else if(this.currentSystem === SystemType.EA)
          this.stageId = 'EAOR';
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription)
      this.subscription.unsubscribe();

    if(this.modeSub)
      this.modeSub.unsubscribe();
  }

  onConfirm(): void {
    if (this.companyName.length > 0) {
      this.bsModalRef.hide();
      this.subject.next({
        ACCEPT: true,
        COMPANY_NAME: this.companyName,
        STAGE: this.stageId,
        REPORT_TYPE: this.reportType
      });
      this.subject.complete();
    }
  }

  onCancel(): void {
    this.bsModalRef.hide();
    this.subject.next({
      ACCEPT: false
    });
    this.subject.complete();
  }

  stageValueChanged(value: SearchableSelection): void {
    this.stageId = value != null ? value.VALUE : '';
  }

  reportTypeChanged(value: SearchableSelection): void {
    this.reportType = value != null ? value.VALUE : null;
  }

  validateInput(): boolean {
    if (!this.companyName || !this.companyName.trim()) {
      return false;
    }

    if (!this.stageId) {
      return false;
    }

    if (!this.reportType) {
      return false;
    }

    if (this.companyName.length > 250) {
      return false;
    }

    return true;
  }

  validLength(): boolean {
    if (this.companyName && this.companyName.length > 250) {
      return false;
    }
    return true;
  }

}
